import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const SummaryTab = ({ trip, theme, lang = "th", onNavigate }) => {
  const [shoppingList, setShoppingList] = useState([]);
  const [isAddingShop, setIsAddingShop] = useState(false);
  const [newShopName, setNewShopName] = useState("");

  const [isDivided, setIsDivided] = useState(false);
  const [divideBy, setDivideBy] = useState(
    trip.members?.length > 0 ? trip.members.length : 1
  );

  // 🌟 State สำหรับการกดคลิกที่กราฟแท่งรายวัน 🌟
  const [selectedBarDate, setSelectedBarDate] = useState(null);

  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    setShoppingList(trip.shoppingList || []);
  }, [trip.shoppingList]);

  // ===================== SHOPPING LIST FUNCTIONS =====================
  const handleToggleShopping = async (id) => {
    const newList = shoppingList.map((item) =>
      item.id === id ? { ...item, isBought: !item.isBought } : item
    );
    setShoppingList(newList);
    trip.shoppingList = newList;
    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
      }
    } catch (error) {
      console.error("Error updating shopping list:", error);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(sortedShoppingList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));
    setShoppingList(updatedItems);
    trip.shoppingList = updatedItems;
    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), {
          shoppingList: updatedItems,
        });
      }
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleAddShoppingItem = async (e) => {
    e.preventDefault();
    if (!newShopName.trim()) return;

    const newItem = {
      id: `shop_${Date.now()}`,
      name: newShopName.trim(),
      isBought: false,
      costTHB: 0,
      notes: "",
      link: "",
      order: shoppingList.length,
    };
    const newList = [...shoppingList, newItem];
    setShoppingList(newList);
    trip.shoppingList = newList;
    setNewShopName("");
    setIsAddingShop(false);
    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
      }
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const sortedShoppingList = [...shoppingList].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number")
      return a.order - b.order;
    return 0;
  });

  // ===================== CALCULATIONS =====================
  const flightTotal = (trip.flightList || []).reduce(
    (sum, f) => sum + (Number(f.price) || 0),
    0
  );
  const stayTotal = (trip.accommodations || []).reduce(
    (sum, a) => sum + (Number(a.totalPrice) || 0),
    0
  );
  const shoppingTotal = (shoppingList || []).reduce(
    (sum, s) => sum + (Number(s.costTHB) || 0),
    0
  );
  const eatingTotal = (trip.itinerary || []).reduce(
    (sum, i) => sum + (Number(i.costTHB) || 0),
    0
  );
  const insuranceTotal = (trip.insurances || []).reduce(
    (sum, i) => sum + (Number(i.price) || 0),
    0
  );

  const expensesList = trip.expenses || [];
  const totalIncome = expensesList
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const miscExpenseTotal = expensesList
    .filter((t) => t.type === "expense")
    .reduce(
      (sum, t) => sum + (Number(t.amountPerPerson) || Number(t.amount) || 0),
      0
    );

  const grandTotal =
    flightTotal +
    stayTotal +
    shoppingTotal +
    eatingTotal +
    insuranceTotal +
    miscExpenseTotal;

  const netTotal = grandTotal - totalIncome;

  // 🌟 (แก้ไขแล้ว) สร้าง sortedFlights กลับคืนมา
  const sortedFlights = [...(trip.flightList || [])].sort(
    (a, b) => new Date(a.depDate) - new Date(b.depDate)
  );

  // ===================== CATEGORY BREAKDOWN =====================
  const categoryTotals = {
    travel: flightTotal,
    stay: stayTotal,
    shopping: shoppingTotal,
    insurance: insuranceTotal,
  };

  (trip.itinerary || []).forEach((item) => {
    const cat = item.categoryId || "other";
    const cost = Number(item.costTHB) || 0;
    categoryTotals[cat] = (categoryTotals[cat] || 0) + cost;
  });

  expensesList.forEach((item) => {
    if (item.type === "expense") {
      const cost = Number(item.amountPerPerson) || Number(item.amount) || 0;
      const cat = item.category || "other";
      categoryTotals[cat] = (categoryTotals[cat] || 0) + cost;
    }
  });

  const categoryMeta = {
    travel: { label: "เดินทาง", color: "#3b82f6", icon: "🚕" },
    food: { label: "อาหาร", color: "#e67e22", icon: "🍔" },
    shopping: { label: "ช้อปปิ้ง", color: "#e74c3c", icon: "🛍️" },
    stay: { label: "ที่พัก", color: "#9b59b6", icon: "🏨" },
    activity: { label: "กิจกรรม", color: "#1abc9c", icon: "🎟️" },
    insurance: { label: "ประกัน", color: "#f59e0b", icon: "🛡️" },
    other: { label: "อื่นๆ", color: "#64748b", icon: "📝" },
  };

  (trip.customCategories || []).forEach((c) => {
    categoryMeta[c.id] = {
      label: c.label,
      color: c.color || "#a855f7",
      icon: c.icon || "🏷️",
    };
  });
  (trip.customExpenseCategories || []).forEach((c) => {
    categoryMeta[c.id] = {
      label: c.label,
      color: c.color || "#2ed573",
      icon: c.icon || "🏷️",
    };
  });

  const breakdownArray = Object.keys(categoryTotals)
    .map((key) => ({
      id: key,
      total: categoryTotals[key],
      ...(categoryMeta[key] || { label: key, color: "#94a3b8", icon: "📌" }),
    }))
    .filter((cat) => cat.total > 0)
    .sort((a, b) => b.total - a.total);

  const topCategory = breakdownArray.length > 0 ? breakdownArray[0] : null;

  // 🌟 ─── คำนวณรายจ่ายรายวัน (Daily Spending & Daily Category Breakdown) ─── 🌟
  const dailyDataMap = {};
  const dailyCategoryMap = {};

  const addDaily = (dateStr, amount, catId) => {
    if (!dateStr || amount <= 0) return;

    // รวมยอดรายวันทั้งหมด
    dailyDataMap[dateStr] = (dailyDataMap[dateStr] || 0) + amount;

    // รวมยอดรายวันแยกตามหมวดหมู่
    if (!dailyCategoryMap[dateStr]) dailyCategoryMap[dateStr] = {};
    dailyCategoryMap[dateStr][catId] =
      (dailyCategoryMap[dateStr][catId] || 0) + amount;
  };

  (trip.flightList || []).forEach((f) =>
    addDaily(f.depDate || trip.start, Number(f.price) || 0, "travel")
  );
  (trip.accommodations || []).forEach((a) =>
    addDaily(a.checkInDate || trip.start, Number(a.totalPrice) || 0, "stay")
  );
  (trip.itinerary || []).forEach((i) =>
    addDaily(i.date, Number(i.costTHB) || 0, i.categoryId || "other")
  );
  expensesList.forEach((e) => {
    if (e.type === "expense")
      addDaily(
        e.date,
        Number(e.amountPerPerson) || Number(e.amount) || 0,
        e.category || "other"
      );
  });

  const dailyDates = Object.keys(dailyDataMap).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  const dailyValues = dailyDates.map((d) => dailyDataMap[d]);
  const maxDaily = dailyValues.length > 0 ? Math.max(...dailyValues) : 1;

  // ===================== EXPANDED VIEW LOGIC (Drill-down) =====================
  const getExpandedItems = () => {
    let items = [];

    if (expandedCategory === "all" || expandedCategory === "flight") {
      (trip.flightList || []).forEach((f) => {
        items.push({
          _origId: f.id,
          _type: "flight",
          id: `f_${f.id}`,
          date: f.depDate || trip.start,
          title: `${f.airline || "สายการบิน"} ${f.flightNo || ""}`,
          note: `${f.depCode || f.depCity || "-"} ➔ ${
            f.arrCode || f.arrCity || "-"
          }`,
          amount: Number(f.price) || 0,
          catId: "travel",
        });
      });
    }

    if (expandedCategory === "all" || expandedCategory === "stay") {
      (trip.accommodations || []).forEach((a) => {
        items.push({
          _origId: a.id,
          _type: "stay",
          id: `a_${a.id}`,
          date: a.checkInDate || trip.start,
          title: a.name,
          note: `เช็คอิน: ${a.checkInDate}`,
          amount: Number(a.totalPrice) || 0,
          catId: "stay",
        });
      });
    }

    if (expandedCategory === "all" || expandedCategory === "shopping") {
      (trip.shoppingList || []).forEach((s) => {
        if (s.costTHB > 0) {
          items.push({
            _origId: s.id,
            _type: "shopping",
            id: `s_${s.id}`,
            date: trip.start, // ให้ลงวันแรกของทริป
            title: s.name,
            note: s.notes,
            amount: Number(s.costTHB) || 0,
            catId: "shopping",
            isBought: s.isBought,
          });
        }
      });
    }

    if (expandedCategory === "all" || expandedCategory === "plan") {
      (trip.itinerary || []).forEach((i) => {
        if (i.costTHB > 0) {
          items.push({
            _origId: i.id,
            _type: "plan",
            id: `i_${i.id}`,
            date: i.date,
            title: i.name,
            note: i.place,
            amount: Number(i.costTHB) || 0,
            catId: i.categoryId || "food",
          });
        }
      });
    }

    if (expandedCategory === "all" || expandedCategory === "expense") {
      (trip.expenses || []).forEach((e) => {
        if (e.type === "expense") {
          items.push({
            _origId: e.id,
            _type: "expense",
            id: `e_${e.id}`,
            date: e.date,
            title: e.title,
            note: e.note,
            amount: Number(e.amountPerPerson) || Number(e.amount) || 0,
            catId: e.category || "other",
            splitCount: e.splitCount,
            originalAmount: e.amount,
            currency: e.currency,
            inputCost: e.inputCost,
          });
        }
      });
    }

    // Sort by Date then Amount
    return items.sort((a, b) => {
      if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
      return b.amount - a.amount;
    });
  };

  const expandedItems = getExpandedItems();

  const handleEditItem = (item) => {
    // ถ้ามีการคลิก จะ Navigate ไปที่แท็บที่รับผิดชอบข้อมูลนั้น
    if (item._type === "flight") onNavigate("flight");
    if (item._type === "stay") onNavigate("stay");
    if (item._type === "shopping") onNavigate("shopping");
    if (item._type === "plan") onNavigate("plan");
    if (item._type === "expense") onNavigate("expense");
  };

  // ─── การตั้งค่า UI ───
  const isDarkMode = theme?.cardBg !== "#fff";
  const bgCard = isDarkMode ? "#1e293b" : "#fff";
  const borderColor = isDarkMode ? "#334155" : "#e2e8f0";

  const cardStyle = {
    backgroundColor: bgCard,
    border: `1px solid ${borderColor}`,
    borderRadius: "16px",
    padding: "20px",
    boxShadow: isDarkMode
      ? "0 4px 20px rgba(0,0,0,0.2)"
      : "0 2px 10px rgba(0,0,0,0.03)",
  };

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${borderColor}`,
    backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
    color: theme?.text || "#000",
    fontSize: "14px",
    outline: "none",
  };

  const getSectionTitle = () => {
    switch (expandedCategory) {
      case "all":
        return "ยอดรวมรายจ่ายทั้งทริป";
      case "flight":
        return "ค่าเดินทาง (Flights)";
      case "stay":
        return "ที่พัก (Accommodation)";
      case "shopping":
        return "ของที่ซื้อก่อนไป (Pre-trip Shopping)";
      case "plan":
        return "กิน/เที่ยว ในทริป (Food & Activities)";
      case "expense":
        return "จิปาถะ / บัญชีทริป (Misc Expenses)";
      default:
        return "";
    }
  };

  return (
    <div style={{ color: theme?.text || "#000", padding: "5px" }}>
      {/* 🌟🌟 ส่วนที่ 1: แผงควบคุมการหาร 🌟🌟 */}
      <div
        className="no-print"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: isDarkMode ? "#1e293b" : "#f1f5f9",
            padding: "8px 16px",
            borderRadius: "999px",
            border: `1px solid ${borderColor}`,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            <input
              type="checkbox"
              checked={isDivided}
              onChange={(e) => setIsDivided(e.target.checked)}
              style={{ accentColor: "#10b981", transform: "scale(1.2)" }}
            />
            หารค่าใช้จ่าย
          </label>
          {isDivided && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: "#888" }}>/</span>
              <input
                type="number"
                min="1"
                value={divideBy}
                onChange={(e) =>
                  setDivideBy(Math.max(1, parseInt(e.target.value) || 1))
                }
                style={{
                  width: "50px",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  border: `1px solid ${borderColor}`,
                  background: isDarkMode ? "#0f172a" : "#fff",
                  color: theme?.text,
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              />
              <span style={{ color: "#888" }}>คน</span>
            </div>
          )}
        </div>
      </div>

      {/* 🌟🌟 ส่วนที่ 2: Dashboard Cards (3 ใบ) 🌟🌟 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "15px",
          marginBottom: "20px",
        }}
      >
        {/* Card 1: รายจ่ายรวม (กดเพื่อดูทั้งหมด) */}
        <div
          onClick={() =>
            setExpandedCategory(expandedCategory === "all" ? null : "all")
          }
          style={{
            ...cardStyle,
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "#fff",
            border: expandedCategory === "all" ? "2px solid #fff" : "none",
            cursor: "pointer",
            transform: expandedCategory === "all" ? "scale(1.02)" : "scale(1)",
            transition: "all 0.2s",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div style={{ fontSize: "13px", opacity: 0.9, fontWeight: "600" }}>
              รายจ่ายรวมทั้งทริป
            </div>
            <div style={{ fontSize: "16px", opacity: 0.8 }}>💰</div>
          </div>
          <div
            style={{ fontSize: "28px", fontWeight: "bold", margin: "8px 0" }}
          >
            ฿
            {grandTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
          {isDivided && divideBy > 1 && (
            <div
              style={{
                fontSize: "13px",
                fontWeight: "bold",
                background: "rgba(255,255,255,0.2)",
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: "6px",
              }}
            >
              หารแล้วตกคนละ ฿
              {(grandTotal / divideBy).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        {/* Card 2: หมวดหมู่สูงสุด */}
        <div style={{ ...cardStyle, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "4px",
              height: "100%",
              background: "#3b82f6",
            }}
          ></div>
          <div
            style={{
              fontSize: "13px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: "600",
            }}
          >
            หมวดหมู่ที่จ่ายเยอะสุด
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              margin: "8px 0",
              color: theme?.text,
            }}
          >
            {topCategory ? topCategory.label : "-"}
          </div>
          <div
            style={{ fontSize: "16px", fontWeight: "bold", color: "#3b82f6" }}
          >
            {topCategory ? `฿${topCategory.total.toLocaleString()}` : "฿0"}
          </div>
        </div>

        {/* Card 3: เงินคงเหลือ (จากกองกลาง) */}
        <div style={{ ...cardStyle, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "4px",
              height: "100%",
              background: netTotal <= 0 ? "#10b981" : "#ef4444",
            }}
          ></div>
          <div
            style={{
              fontSize: "13px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: "600",
            }}
          >
            เงินกองกลางคงเหลือ (ส่วนต่าง)
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              margin: "8px 0",
              color: netTotal <= 0 ? "#10b981" : "#ef4444",
            }}
          >
            {netTotal <= 0 ? "+" : "-"}฿
            {Math.abs(netTotal).toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: isDarkMode ? "#64748b" : "#94a3b8",
            }}
          >
            รายรับกองกลาง: ฿{totalIncome.toLocaleString()}
          </div>
        </div>
      </div>

      {/* 🌟🌟 ส่วนที่ 3: ปุ่มเลือกหมวดหมู่ย่อย (เพื่อกดดูรายการแบบละเอียด) 🌟🌟 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: "10px",
          marginBottom: "25px",
        }}
      >
        {[
          {
            id: "flight",
            label: "ค่าเดินทาง",
            total: flightTotal,
            color: "#3498db",
          },
          { id: "stay", label: "ที่พัก", total: stayTotal, color: "#9b59b6" },
          {
            id: "shopping",
            label: "ของซื้อก่อนไป",
            total: shoppingTotal,
            color: "#e74c3c",
          },
          {
            id: "plan",
            label: "กิน/เที่ยว",
            total: eatingTotal,
            color: "#e67e22",
          },
          {
            id: "expense",
            label: "จิปาถะ",
            total: miscExpenseTotal,
            color: "#2ed573",
          },
        ].map((btn) => (
          <div
            key={btn.id}
            onClick={() =>
              setExpandedCategory(expandedCategory === btn.id ? null : btn.id)
            }
            style={{
              backgroundColor:
                expandedCategory === btn.id ? `${btn.color}20` : bgCard,
              border:
                expandedCategory === btn.id
                  ? `2px solid ${btn.color}`
                  : `1px solid ${borderColor}`,
              borderRadius: "12px",
              padding: "12px",
              cursor: "pointer",
              transition: "all 0.2s",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: isDarkMode ? "#cbd5e1" : "#64748b",
                fontWeight: "bold",
                marginBottom: "4px",
              }}
            >
              {btn.label}
            </div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: btn.color }}
            >
              ฿
              {btn.total.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          </div>
        ))}
      </div>

      {/* 🌟 รายการแบบละเอียด (Expanded View) เมื่อกดจากปุ่มด้านบน 🌟 */}
      {expandedCategory && (
        <div
          style={{
            ...cardStyle,
            border: `2px solid ${
              expandedCategory === "all" ? "#10b981" : "#3b82f6"
            }`,
            marginBottom: "25px",
            animation: "fadeIn 0.3s",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
              borderBottom: `1px solid ${borderColor}`,
              paddingBottom: "10px",
            }}
          >
            <h4 style={{ margin: 0, color: theme?.text, fontSize: "16px" }}>
              📋 รายละเอียด: {getSectionTitle()}
            </h4>
            <button
              onClick={() => setExpandedCategory(null)}
              style={{
                background: "none",
                border: "none",
                color: "#888",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>

          {expandedItems.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {expandedItems.map((item) => {
                const catMeta = categoryMeta[item.catId] || {
                  icon: "📌",
                  color: "#7f8c8d",
                };
                return (
                  <div
                    key={item.id}
                    onClick={() => handleEditItem(item)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: isDarkMode ? "#0f172a" : "#f8fafc",
                      padding: "14px 16px",
                      borderRadius: "12px",
                      border: `1px solid ${borderColor}`,
                      borderLeft: `4px solid ${catMeta.color}`,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "none";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "20px",
                          background: `${catMeta.color}15`,
                          padding: "8px",
                          borderRadius: "50%",
                          width: "38px",
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {catMeta.icon}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: theme?.text,
                            textDecoration: item.isBought
                              ? "line-through"
                              : "none",
                          }}
                        >
                          {item.title}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            fontSize: "11px",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            marginTop: "2px",
                          }}
                        >
                          <span>{item.date}</span>
                          {item.note && <span>• {item.note}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#ef4444",
                        }}
                      >
                        -฿
                        {item.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                      {item.currency === "JPY" && (
                        <div style={{ fontSize: "10px", color: "#888" }}>
                          (¥{item.inputCost?.toLocaleString()})
                        </div>
                      )}
                      {item.splitCount > 1 && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#e67e22",
                            fontWeight: "bold",
                          }}
                        >
                          หาร {item.splitCount} (จาก ฿{item.originalAmount})
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{ textAlign: "center", color: "#888", padding: "20px" }}
            >
              ไม่มีรายการในหมวดหมู่นี้
            </div>
          )}
        </div>
      )}

      {/* 🌟🌟 ส่วนที่ 4: กราฟ (Charts Section) 🌟🌟 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "15px",
          marginBottom: "25px",
        }}
      >
        {/* Left: Bar Chart (Daily Spending Trend) */}
        <div style={{ ...cardStyle, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            📈 แนวโน้มรายจ่ายรายวัน (Daily Spending Trend)
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "10px",
              height: "180px",
              paddingBottom: "25px",
              position: "relative",
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            {dailyDates.length > 0 ? (
              dailyDates.map((date) => {
                const val = dailyDataMap[date];
                const heightPercent = (val / maxDaily) * 100;
                const shortDate = new Date(date).toLocaleDateString("th-TH", {
                  day: "numeric",
                  month: "short",
                });

                const isSelected = selectedBarDate === date;

                return (
                  <div
                    key={date}
                    onClick={() =>
                      setSelectedBarDate(selectedBarDate === date ? null : date)
                    } // 🌟 Click to toggle daily breakdown
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      height: "100%",
                      position: "relative",
                      cursor: "pointer",
                      opacity: selectedBarDate ? (isSelected ? 1 : 0.4) : 1, // Highlight if selected
                      transition: "opacity 0.2s, transform 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        color: isSelected ? theme?.text : "#888",
                        fontWeight: isSelected ? "bold" : "normal",
                        marginBottom: "4px",
                      }}
                    >
                      {val >= 1000
                        ? `${(val / 1000).toFixed(1)}k`
                        : val.toLocaleString()}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "30px",
                        height: `${heightPercent}%`,
                        background: isSelected
                          ? "linear-gradient(to top, #f59e0b, #fbbf24)" // สีเหลืองเมื่อถูกเลือก
                          : "linear-gradient(to top, #3b82f6, #60a5fa)",
                        borderRadius: "4px 4px 0 0",
                      }}
                    ></div>
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-22px",
                        fontSize: "10px",
                        color: isSelected
                          ? theme?.text
                          : isDarkMode
                          ? "#94a3b8"
                          : "#64748b",
                        fontWeight: isSelected ? "bold" : "normal",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {shortDate}
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  width: "100%",
                  textAlign: "center",
                  color: "#888",
                  alignSelf: "center",
                }}
              >
                ไม่มีข้อมูลรายจ่าย
              </div>
            )}
          </div>
        </div>

        {/* Right: Donut Chart (Category Breakdown) */}
        <div style={{ ...cardStyle, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            📊 สัดส่วนค่าใช้จ่าย (Monthly Categories)
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              flex: 1,
            }}
          >
            <div
              style={{
                position: "relative",
                width: "140px",
                height: "140px",
                flexShrink: 0,
              }}
            >
              <svg
                viewBox="0 0 42 42"
                width="100%"
                height="100%"
                style={{ transform: "rotate(-90deg)" }}
              >
                {grandTotal === 0 ? (
                  <circle
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke={borderColor}
                    strokeWidth="6"
                  />
                ) : (
                  (() => {
                    let cumulativePercent = 0;
                    return breakdownArray.map((cat, i) => {
                      const percent = (cat.total / grandTotal) * 100;
                      const strokeDasharray = `${Math.max(0, percent - 1)} ${
                        100 - Math.max(0, percent - 1)
                      }`;
                      const strokeDashoffset = 100 - cumulativePercent;
                      cumulativePercent += percent;
                      return (
                        <circle
                          key={cat.id}
                          cx="21"
                          cy="21"
                          r="15.915"
                          fill="transparent"
                          stroke={cat.color}
                          strokeWidth="6"
                          strokeDasharray={strokeDasharray}
                          strokeDashoffset={strokeDashoffset}
                          style={{ transition: "stroke-dasharray 0.5s ease" }}
                        />
                      );
                    });
                  })()
                )}
              </svg>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                  }}
                >
                  TOTAL
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: theme?.text,
                  }}
                >
                  {grandTotal >= 10000
                    ? `${(grandTotal / 1000).toFixed(1)}k`
                    : grandTotal.toLocaleString()}
                </div>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                maxHeight: "150px",
                overflowY: "auto",
                paddingRight: "5px",
              }}
            >
              {breakdownArray.map((cat) => {
                const percent =
                  grandTotal > 0
                    ? ((cat.total / grandTotal) * 100).toFixed(1)
                    : 0;
                return (
                  <div
                    key={cat.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: cat.color,
                        }}
                      ></div>
                      <span
                        style={{
                          color: isDarkMode ? "#cbd5e1" : "#334155",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "80px",
                        }}
                      >
                        {cat.label}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      <span style={{ color: theme?.text }}>
                        ฿{cat.total.toLocaleString()}
                      </span>
                      <span
                        style={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          width: "35px",
                          textAlign: "right",
                        }}
                      >
                        {percent}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 🌟🌟 ส่วนที่ 4.5: กล่องแสดงหมวดหมู่รายจ่ายของวันที่เลือกจาก Bar Chart 🌟🌟 */}
      {selectedBarDate && dailyCategoryMap[selectedBarDate] && (
        <div
          style={{
            ...cardStyle,
            marginTop: "0",
            marginBottom: "25px",
            border: `2px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
            animation: "fadeIn 0.3s",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h4
              style={{
                margin: 0,
                color: theme?.text,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>🔍</span>
              ค่าใช้จ่ายวันที่{" "}
              {new Date(selectedBarDate).toLocaleDateString("th-TH", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </h4>
            <button
              onClick={() => setSelectedBarDate(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
                color: "#888",
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {Object.entries(dailyCategoryMap[selectedBarDate])
              .sort((a, b) => b[1] - a[1]) // เรียงจากยอดเยอะไปน้อย
              .map(([catId, amount]) => {
                const meta = categoryMeta[catId] || {
                  label: "อื่นๆ",
                  icon: "📌",
                  color: "#94a3b8",
                };
                return (
                  <div
                    key={catId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 14px",
                      borderRadius: "10px",
                      background: `${meta.color}15`,
                      border: `1px solid ${meta.color}`,
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>{meta.icon}</span>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: theme?.text,
                      }}
                    >
                      {meta.label}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: meta.color,
                      }}
                    >
                      ฿
                      {amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* 🌟🌟 ส่วนที่ 5: ปฏิทินเที่ยวบิน 🌟🌟 */}
      <h3
        style={{
          marginBottom: "15px",
          borderTop: `1px dashed ${borderColor}`,
          paddingTop: "20px",
        }}
      >
        🗓️ ปฏิทินเที่ยวบิน
      </h3>
      <div style={{ ...cardStyle, marginBottom: "25px" }}>
        {sortedFlights.length > 0 ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {sortedFlights.map((f, idx) => {
              const dateObj = f.depDate ? new Date(f.depDate) : null;
              return (
                <div
                  key={f.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    padding: "10px",
                    borderRadius: "10px",
                    backgroundColor: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "#f8fafc",
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#10b981",
                      color: "#fff",
                      borderRadius: "8px",
                      minWidth: "60px",
                      height: "60px",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      {dateObj
                        ? dateObj.toLocaleDateString(
                            lang === "th" ? "th-TH" : "en-US",
                            { month: "short" }
                          )
                        : "-"}
                    </span>
                    <span
                      style={{
                        fontSize: "22px",
                        fontWeight: "bold",
                        lineHeight: "1",
                      }}
                    >
                      {dateObj ? dateObj.getDate() : "-"}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "bold",
                        marginBottom: "4px",
                      }}
                    >
                      {f.airline || "ไม่ระบุสายการบิน"}{" "}
                      <span style={{ color: "#888", fontSize: "13px" }}>
                        {f.flightNo}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: isDarkMode ? "#cbd5e1" : "#475569",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        🛫 <strong>{f.depCode || f.depCity || "-"}</strong> (
                        {f.depTime || "00:00"})
                      </span>
                      <span style={{ color: "#94a3b8" }}>➔</span>
                      <span>
                        🛬 <strong>{f.arrCode || f.arrCity || "-"}</strong> (
                        {f.arrTime || "00:00"})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              fontSize: "13px",
              color: "#888",
              textAlign: "center",
              padding: "10px 0",
            }}
          >
            ยังไม่มีข้อมูลเที่ยวบิน
          </div>
        )}
      </div>

      {/* 🌟🌟 ส่วนที่ 6: รายการเตรียมของ (Checklist) 🌟🌟 */}
      <h3
        style={{
          marginBottom: "15px",
          borderTop: `1px dashed ${borderColor}`,
          paddingTop: "20px",
        }}
      >
        🧳 รายการเตรียมของ (Checklist)
      </h3>
      <div style={{ ...cardStyle, marginBottom: "25px" }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="summary-shopping-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "10px",
                }}
              >
                {sortedShoppingList.length > 0 ? (
                  sortedShoppingList.map((item, idx) => (
                    <Draggable
                      key={String(item.id)}
                      draggableId={String(item.id)}
                      index={idx}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            backgroundColor: item.isBought
                              ? isDarkMode
                                ? "rgba(16,185,129,0.1)"
                                : "#f0fdf4"
                              : isDarkMode
                              ? "#0f172a"
                              : "#f8fafc",
                            border: `1px solid ${
                              item.isBought ? "transparent" : borderColor
                            }`,
                            boxShadow: snapshot.isDragging
                              ? "0 5px 15px rgba(0,0,0,0.15)"
                              : "none",
                            opacity: snapshot.isDragging ? 0.8 : 1,
                          }}
                        >
                          <div
                            className="no-print"
                            {...provided.dragHandleProps}
                            style={{
                              color: "#bbb",
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            ⋮⋮
                          </div>
                          <input
                            type="checkbox"
                            checked={!!item.isBought}
                            onChange={() => handleToggleShopping(item.id)}
                            style={{
                              width: "18px",
                              height: "18px",
                              accentColor: "#10b981",
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            onClick={() => handleToggleShopping(item.id)}
                            style={{
                              fontSize: "14px",
                              color: item.isBought ? "#10b981" : theme?.text,
                              textDecoration: item.isBought
                                ? "line-through"
                                : "none",
                              fontWeight: item.isBought ? "600" : "500",
                              flex: 1,
                              cursor: "pointer",
                            }}
                          >
                            {item.name}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      fontSize: "13px",
                      color: "#888",
                      textAlign: "center",
                      padding: "10px 0",
                    }}
                  >
                    ยังไม่มีรายการเตรียมของ
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="no-print" style={{ marginTop: "15px" }}>
          {isAddingShop ? (
            <form
              onSubmit={handleAddShoppingItem}
              style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
            >
              <input
                type="text"
                placeholder="พิมพ์ชื่อสิ่งของแล้วกด Enter..."
                value={newShopName}
                onChange={(e) => setNewShopName(e.target.value)}
                autoFocus
                style={{ ...inputStyle, flex: "1 1 200px" }}
              />
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#10b981",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                เพิ่ม
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAddingShop(false);
                  setNewShopName("");
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: `1px solid ${borderColor}`,
                  background: "transparent",
                  color: theme?.text,
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ยกเลิก
              </button>
            </form>
          ) : (
            <button
              onClick={() => setIsAddingShop(true)}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: `1px dashed ${borderColor}`,
                background: "transparent",
                color: theme?.text,
                fontSize: "13px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              ➕ เพิ่มรายการด่วน
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
